import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

// Layout / shared
import { PageHeader } from "../components/PageSpecificComponents";

// Analytics components
import {
  DashboardFilters,
  LiveIndicator,
  SectionTitle,
  KpiRow,
  ResponseTimeChart,
  DurationByTypeChart,
  DeclineReasonsChart,
  RatingDistribution,
  RatingTrend,
  TechnicianRanking,
  RecentLowRatings
} from "../components/PageSpecificComponents/pagesComponents/analytics";

// API
import {
  exportAnalythicsPDF,
  fetchAnalyticsOverview,
  fetchAnalyticsOperations,
  fetchAnalyticsRatings
} from "../api/calls/analythics.call";

// Constants & helpers
import { resolveDateRange } from "../constants/analytics.constants";

// Styles
import { AnalyticsStyles } from "../pagesStyles/analytics.styles";
// Upsell state reuses the billing design tokens (all scoped under .tf-billing).
import "../styles/billing.css";

// Auto-refresh interval (15 minutes). Matches the footer note shown in
// the design — also gives breathing room for the 3-call fan-out.
const REFRESH_MS = 15 * 60 * 1000;

const Analytics = () => {
  // Filters state
  const [branchId, setBranchId] = useState(null);
  const [rangePreset, setRangePreset] = useState("30d");
  const [customRange, setCustomRange] = useState({});
  const [compare, setCompare] = useState(true);

  // Dashboard data state
  const [overview, setOverview] = useState(null);
  const [operations, setOperations] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(new Date());

  // Loading state per section
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingOperations, setLoadingOperations] = useState(true);
  const [loadingRatings, setLoadingRatings] = useState(true);

  // Export busy flag — disables the Export button while the BE
  // generates the PDF (~1-3s because charts are rendered to PNGs).
  const [exporting, setExporting] = useState(false);

  // Service types lookup (used for human-readable labels in widgets)
  const serviceTypes = useSelector(
    state => state.app.systemComponents?.serviceTypes || []
  );

  // `=== false` so the upsell doesn't flash while dashboardData loads.
  const locked = useSelector(
    state => state.app.dashboardData?.accessToAnalytics === false
  );

  // Resolve preset → concrete date range
  const resolvedRange = useMemo(
    () => resolveDateRange(rangePreset, customRange),
    [rangePreset, customRange]
  );

  // Custom range needs both endpoints filled in. Until then we hold off
  // calling the BE — sending half a range (or `undefined`) would either
  // return a no-op result or implicitly fall back to "all time", neither
  // of which matches user intent.
  const filtersReady =
    rangePreset !== "custom" ||
    (!!resolvedRange.dateFrom && !!resolvedRange.dateTo);

  // Build the filter payload sent to the BE
  const filters = useMemo(
    () => ({
      branchId: branchId || undefined,
      dateFrom: resolvedRange.dateFrom,
      dateTo: resolvedRange.dateTo
    }),
    [branchId, resolvedRange]
  );

  // ---- Data loaders -----

  const loadOverview = useCallback(async () => {
    setLoadingOverview(true);
    const res = await fetchAnalyticsOverview({ ...filters, compare });
    if (res.hasError) {
      toast.error(res.error?.message || "Failed to load overview");
      setOverview(null);
    } else {
      setOverview(res.overview);
    }
    setLoadingOverview(false);
  }, [filters, compare]);

  const loadOperations = useCallback(async () => {
    setLoadingOperations(true);
    const res = await fetchAnalyticsOperations(filters);
    if (res.hasError) {
      toast.error(res.error?.message || "Failed to load operations");
      setOperations(null);
    } else {
      setOperations(res.operations);
    }
    setLoadingOperations(false);
  }, [filters]);

  const loadRatings = useCallback(async () => {
    setLoadingRatings(true);
    const res = await fetchAnalyticsRatings(filters);
    if (res.hasError) {
      toast.error(res.error?.message || "Failed to load ratings");
      setRatings(null);
    } else {
      setRatings(res.ratings);
    }
    setLoadingRatings(false);
  }, [filters]);

  const loadAll = useCallback(async () => {
    // Skip the fan-out while the user is mid-pick on a custom date
    // range — calling the BE with `undefined` dates would just clear
    // the dashboard for no good reason. Locked plans would only collect
    // three 403 toasts.
    if (!filtersReady || locked) return;

    // allSettled (not Promise.all) — one failing widget shouldn't kill
    // the rest. Each loader already toast.error()'s on its own failure
    // and clears its slice of state, so a partial dashboard is the
    // expected graceful-degradation outcome. We always update the
    // "Updated" timestamp so the user sees a fresh refresh attempt.
    await Promise.allSettled([
      loadOverview(),
      loadOperations(),
      loadRatings()
    ]);
    setUpdatedAt(new Date());
  }, [filtersReady, locked, loadOverview, loadOperations, loadRatings]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // 15-minute auto-refresh
  useEffect(() => {
    const id = setInterval(() => {
      loadAll();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [loadAll]);

  // ---- Export -----

  const exportPDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await exportAnalythicsPDF(
        branchId || "",
        resolvedRange.dateFrom || "",
        resolvedRange.dateTo || "",
        compare
      );
      if (res.hasError) {
        toast.error(res.error?.message || "Export failed");
        return;
      }
      const fileURL = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = fileURL;
      // Filename carries the applied filters so a downloaded report
      // can be re-identified later (branch + period). Underscored to
      // play nice across file managers.
      const safeBranch = branchId ? `b${branchId}` : "all";
      const from = resolvedRange.dateFrom || "any";
      const to = resolvedRange.dateTo || "any";
      a.download = `analytics_${safeBranch}_${from}_${to}.pdf`;
      a.click();
      window.URL.revokeObjectURL(fileURL);
    } finally {
      setExporting(false);
    }
  };

  // Feature-level upsell — the plan doesn't include analytics.
  if (locked) {
    return (
      <AnalyticsStyles>
        <PageHeader title="Analytics" />
        <div className="tf-billing">
          <div className="panel plan-upsell">
            <div className="panel-body">
              <h2 className="tf-title">
                Analytics is available on Standard and above
              </h2>
              <p className="tf-subtitle">
                Upgrade your plan to unlock performance KPIs, response times,
                decline reasons and customer ratings across your branches.
              </p>
              <Link className="btn btn-primary btn-lg" to="/subscription-plans">
                Upgrade plan
              </Link>
            </div>
          </div>
        </div>
      </AnalyticsStyles>
    );
  }

  return (
    <AnalyticsStyles>
      <PageHeader title="Analytics">
        <LiveIndicator updatedAt={updatedAt} />
      </PageHeader>

      <DashboardFilters
        branchId={branchId}
        onBranchChange={setBranchId}
        rangePreset={rangePreset}
        onRangePresetChange={setRangePreset}
        customRange={customRange}
        onCustomRangeChange={setCustomRange}
        compare={compare}
        onCompareChange={setCompare}
        onExport={exportPDF}
        exporting={exporting}
      />

      {/* Overview */}
      <SectionTitle eyebrow="Overview" title="Key performance indicators" />
      <KpiRow
        overview={overview}
        loading={loadingOverview}
        showCompare={compare}
      />

      {/* Operations */}
      <SectionTitle
        eyebrow="Operations"
        title="How fast and how well are we serving?"
      />
      <div className="grid-2">
        <ResponseTimeChart data={operations?.responseTimeTrend || []} />
        <DurationByTypeChart
          data={operations?.durationByServiceType || []}
          serviceTypes={serviceTypes}
        />
      </div>
      <div className="grid-1 row-spacer">
        <DeclineReasonsChart data={operations?.declineReasons || []} />
      </div>

      {/* Customer feedback */}
      <SectionTitle
        eyebrow="Customer feedback"
        title="Ratings & technician performance"
      />
      <div className="grid-2 grid-2--ratings">
        <RatingDistribution data={ratings?.distribution || []} />
        <RatingTrend data={ratings?.trend || []} />
      </div>
      <div className="row-spacer">
        <TechnicianRanking data={ratings?.technicianRanking || []} />
      </div>
      <div className="row-spacer">
        <RecentLowRatings
          data={ratings?.recentLowRatings || []}
          loading={loadingRatings}
          serviceTypes={serviceTypes}
        />
      </div>

      <footer className="dash-footer">
        <span>TruckFix ERP · Analytics v2.0</span>
        <span>Data refreshes every 15 min</span>
      </footer>
    </AnalyticsStyles>
  );
};

export default Analytics;
